import {
    UserPersonalInformationDto,
    UserPassportDetailsDto,
    UserResidentialStatusDto,
    UserTaxInfoDto,
    UserBankDetailsDto,
    UserContactDetailsDto,
    UserNominationDto,
    UserRiskProfileDto,
    InvestorExperienceDto
} from '../services/profile.service';

// Regex Patterns
/** RBI IFSC: 4 letters (bank code) + '0' (reserved) + 6 alphanumerics. */
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export const NAME_REGEX = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\s'.-]{2,100}$/;
export const PHONE_DIGITS_REGEX = /^\d+$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
/** Laravel zip rule (contact + bank + nominee): 3-10 chars — letters, digits, spaces, hyphens. */
export const ZIP_CODE_REGEX = /^[A-Za-z0-9 \-]{3,10}$/;
/** Laravel bank-name rule: strictly letters and spaces. */
export const LETTERS_SPACES_REGEX = /^[a-zA-Z\s]+$/;

// Common Validation Helpers
const isEmpty = (value: any) => !value || (typeof value === 'string' && value.trim() === '');

const validateName = (value: string | undefined, label: string): string | null => {
    if (isEmpty(value)) return `${label} is required`;
    if (!NAME_REGEX.test(value!)) return `${label} contains invalid characters`;
    return null;
};

const validateDate = (value: string | undefined, label: string): string | null => {
    if (isEmpty(value)) return `${label} is required`;
    return null;
};

/** Today (plus `offsetDays`) as ISO yyyy-mm-dd in the USER'S local calendar (not UTC). */
export const localTodayIso = (offsetDays = 0): string => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Helper to add error
/**
 * Completed years between a date of birth and today, counted on the CALENDAR.
 *
 * Personal Info previously derived the investor's age from the epoch difference
 * (`Date.now() - dob` re-read as a year offset). A date of birth parses as UTC midnight while
 * "now" is a real instant, so an investor in IST checking before ~05:30 on their 18th birthday
 * was told they were 17 — rejected on the very day they became eligible. The nominee and
 * guardian checks already counted calendar years; this is the same arithmetic, shared, so every
 * age rule in the app agrees.
 *
 * Turning 18 today counts as 18, which is what Laravel's rule (`dob > today - 18y`) intends.
 */
export const ageInYears = (dob: string | Date): number => {
    const d = dob instanceof Date ? dob : new Date(dob);
    if (isNaN(d.getTime())) return NaN;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
};

const addError = (errors: Record<string, string>, field: string, message: string) => {
    errors[field] = message;
};

export const validatePersonalInformation = (data: UserPersonalInformationDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Name
    let err = validateName(data.investorFirstName, 'First Name');
    if (err) addError(errors, 'investorFirstName', err);

    err = validateName(data.investorLastName, 'Last Name');
    if (err) addError(errors, 'investorLastName', err);

    // Date of Birth & Age Check
    if (isEmpty(data.userDob)) {
        addError(errors, 'userDob', 'Date of Birth is required');
    } else if (ageInYears(data.userDob!) < 18) {
        addError(errors, 'userDob', 'You must be at least 18 years old');
    }

    if (isEmpty(data.gender)) addError(errors, 'gender', 'Gender is required');
    if (isEmpty(data.maritalStatus)) addError(errors, 'maritalStatus', 'Marital Status is required');
    // Laravel parity (personal_information_submit): city_of_dob regex letters+spaces only.
    if (isEmpty(data.cityOfDob)) {
        addError(errors, 'cityOfDob', 'City of Birth is required');
    } else if (!/^[a-zA-Z\s]+$/.test(data.cityOfDob!)) {
        addError(errors, 'cityOfDob', 'City of Birth must contain only letters and spaces.');
    }
    if (isEmpty(data.countryDob)) addError(errors, 'countryDob', 'Country of Birth is required');
    if (isEmpty(data.citizenship)) addError(errors, 'citizenship', 'Citizenship is required');
    if (isEmpty(data.countryOfResidence) || String(data.countryOfResidence).trim() === '-1') {
        addError(errors, 'countryOfResidence', 'Country of Residence is required');
    }

    // Parents
    err = validateName(data.fathersFirstName, "Father's First Name");
    if (err) addError(errors, 'fathersFirstName', err);

    err = validateName(data.fathersLastName, "Father's Last Name");
    if (err) addError(errors, 'fathersLastName', err);

    err = validateName(data.motherFirstName, "Mother's First Name");
    if (err) addError(errors, 'motherFirstName', err);

    err = validateName(data.motherLastName, "Mother's Last Name");
    if (err) addError(errors, 'motherLastName', err);

    // PAN — Laravel parity: required, exactly 10 chars, ABCDE1234F format. The field
    // displays uppercase (global visual transform), so normalise before testing.
    const pan = (data.panNumber ?? '').trim().toUpperCase();
    if (!pan) {
        addError(errors, 'panNumber', 'PAN number is required.');
    } else if (pan.length !== 10) {
        addError(errors, 'panNumber', 'PAN number must be exactly 10 characters.');
    } else if (!PAN_REGEX.test(pan)) {
        addError(errors, 'panNumber', 'Invalid PAN number format. Example: ABCDE1234F');
    }

    // Maiden name — Laravel parity: REQUIRED (first + last) only for female applicants
    // with marital status 2 or 5; for everyone else optional, but name-regex checked
    // when typed. Visibility of the block is the form's concern; requirement is this
    // exact Laravel condition so e.g. a divorced female investor is not forced to fill it.
    const maidenRequired =
        String(data.gender ?? '').toUpperCase() === 'FEMALE'
        && ['2', '5'].includes(String(data.maritalStatus ?? ''));
    if (maidenRequired) {
        if (isEmpty(data.maidenName)) {
            addError(errors, 'maidenName', 'Maiden First Name is required for married female applicants.');
        } else if (!NAME_REGEX.test(data.maidenName!)) {
            addError(errors, 'maidenName', 'Maiden First Name must contain only letters and spaces.');
        }
        if (isEmpty(data.maidenLastName)) {
            addError(errors, 'maidenLastName', 'Maiden Last Name is required for married female applicants.');
        } else if (!NAME_REGEX.test(data.maidenLastName!)) {
            addError(errors, 'maidenLastName', 'Maiden Last Name must contain only letters and spaces.');
        }
    } else {
        if (data.maidenName && !NAME_REGEX.test(data.maidenName)) {
            addError(errors, 'maidenName', 'Maiden First Name must contain only letters and spaces.');
        }
        if (data.maidenLastName && !NAME_REGEX.test(data.maidenLastName)) {
            addError(errors, 'maidenLastName', 'Maiden Last Name must contain only letters and spaces.');
        }
    }

    // Address lives on the Contact Details tab (Laravel parity) and is validated there.

    return errors;
};

/**
 * @param skipNonResidentFields true when the investor type doesn't show the
 * "Date of becoming Non-Resident" / "Years abroad" inputs — Laravel hides them
 * for OCI.
 */
export const validatePassportInformation = (
    data: UserPassportDetailsDto,
    skipNonResidentFields = false
): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (isEmpty(data.passportNumber)) addError(errors, 'passportNumber', 'Passport Number is required');
    if (isEmpty(data.passportIssueDate)) addError(errors, 'passportIssueDate', 'Passport Issue Date is required');
    if (isEmpty(data.passportExpiryDate)) addError(errors, 'passportExpiryDate', 'Passport Expiry Date is required');

    // Laravel: Place of Issue must contain only alphabets/spaces.
    if (isEmpty(data.passportPlaceOfIssue)) {
        addError(errors, 'passportPlaceOfIssue', 'Place of Issue is required');
    } else if (!/^[A-Za-z\s]+$/.test(data.passportPlaceOfIssue!)) {
        addError(errors, 'passportPlaceOfIssue', 'Place of Issue must contain only alphabets');
    }

    // Nationality is derived + read-only (Laravel) — not a user-validated field.
    // Country of Issue is not on the Laravel screen; it arrives via OCR import.

    if (!skipNonResidentFields) {
        if (isEmpty(data.passportDateNonResident)) {
            addError(errors, 'passportDateNonResident', 'Date of becoming Non-Resident is required');
        } else if (data.passportDateNonResident! > localTodayIso()) {
            // Laravel parity: the blade picker caps this at today and clears future entries.
            addError(errors, 'passportDateNonResident', 'Date of becoming Non-Resident cannot be in the future');
        }
        const years = data.passportNoYearsAbroad;
        if (years === undefined || years === null || String(years) === '') {
            addError(errors, 'passportNoYearsAbroad', 'Number of years living abroad is required');
        } else if (Number.isNaN(Number(years)) || Number(years) > 90) {
            addError(errors, 'passportNoYearsAbroad', 'Number of years abroad must be a number (max 90)');
        }
    }

    // Date logic — Laravel parity (blade validatePassportDates, information-update.blade
    // ~4800): issue strictly in the past, expiry strictly in the future, expiry strictly
    // after issue (equal dates rejected), and expiry at most 10 years from issue.
    // Values are ISO yyyy-mm-dd so string comparison is safe; today is computed in the
    // user's local calendar (toISOString would shift a day for evening IST users).
    const todayIso = localTodayIso();
    if (data.passportIssueDate && data.passportIssueDate >= todayIso) {
        addError(errors, 'passportIssueDate', 'Issue Date cannot be today or in future.');
    }
    if (data.passportExpiryDate && data.passportExpiryDate <= todayIso) {
        addError(errors, 'passportExpiryDate', 'Expiry Date cannot be today or in past.');
    }
    if (data.passportIssueDate && data.passportExpiryDate && !errors.passportExpiryDate) {
        if (data.passportExpiryDate <= data.passportIssueDate) {
            addError(errors, 'passportExpiryDate', 'Expiry Date must be after Issue Date.');
        } else {
            const maxExpiry = new Date(data.passportIssueDate);
            maxExpiry.setFullYear(maxExpiry.getFullYear() + 10);
            if (new Date(data.passportExpiryDate) > maxExpiry) {
                addError(errors, 'passportExpiryDate', 'Passport expiry cannot exceed 10 years from issue.');
            }
        }
    }

    return errors;
};

/**
 * Laravel renders this tab strictly by investor type (blade Section3): the OCI
 * card block for OCI, Type of Proof plus its visa / resident-card block for
 * NRI, and nothing at all for any other type. Validation follows the same
 * split so a hidden field can never block a save.
 */
export const validateResidentialStatus = (
    data: UserResidentialStatusDto,
    opts: { isOci?: boolean; isNri?: boolean } = {}
): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Date edges — Laravel blade calendar parity. Unlike passport, TODAY IS ALLOWED on
    // both sides here (flatpickr maxDate/minDate "today"): issue-type dates must not be
    // in the future, expiry-type dates must not be in the past. Optional dates are
    // checked only when filled.
    const todayIso = localTodayIso();

    if (opts.isOci) {
        // Laravel: OCI Card No + Issue Date are required; Valid Upto is not.
        if (isEmpty(data.userOciCardNo)) addError(errors, 'userOciCardNo', 'OCI Card No is required');
        if (isEmpty(data.userOciIssueDate)) {
            addError(errors, 'userOciIssueDate', 'Issue Date is required');
        } else if (data.userOciIssueDate! > todayIso) {
            addError(errors, 'userOciIssueDate', 'Issue Date cannot be in the future');
        }
        if (data.userOciValidUpto && data.userOciValidUpto < todayIso) {
            addError(errors, 'userOciValidUpto', 'Valid Upto cannot be in the past');
        }
        return errors;
    }

    if (opts.isNri) {
        if (isEmpty(data.userTypeOfProof)) {
            addError(errors, 'userTypeOfProof', 'Type of Proof is required');
            return errors;
        }
        if (data.userTypeOfProof === 'Visa') {
            if (isEmpty(data.userVisaType)) addError(errors, 'userVisaType', 'Visa Type is required');
            if (isEmpty(data.userVisaNumber)) addError(errors, 'userVisaNumber', 'Visa Number is required');
            if (isEmpty(data.userVisaIssuerDate)) {
                addError(errors, 'userVisaIssuerDate', 'Visa Issuer Date is required');
            } else if (data.userVisaIssuerDate! > todayIso) {
                addError(errors, 'userVisaIssuerDate', 'Visa Issuer Date cannot be in the future');
            }
            if (isEmpty(data.userVisaExpiryDate)) {
                addError(errors, 'userVisaExpiryDate', 'Visa Expiry Date is required');
            } else if (data.userVisaExpiryDate! < todayIso) {
                addError(errors, 'userVisaExpiryDate', 'Visa Expiry Date cannot be in the past');
            } else if (data.userVisaIssuerDate && data.userVisaExpiryDate! < data.userVisaIssuerDate) {
                // Laravel: the expiry calendar's floor follows the issuer date (equal allowed).
                addError(errors, 'userVisaExpiryDate', 'Visa Expiry Date cannot be before the Issuer Date');
            }
        }
        // Laravel: only Date of Issue is required for the resident-card block.
        if (data.userTypeOfProof === 'Resident Proof') {
            if (isEmpty(data.userVisaDateOfIssue)) {
                addError(errors, 'userVisaDateOfIssue', 'Date of Issue is required');
            } else if (data.userVisaDateOfIssue! > todayIso) {
                addError(errors, 'userVisaDateOfIssue', 'Date of Issue cannot be in the future');
            }
            if (data.userVisaValidUpto && data.userVisaValidUpto < todayIso) {
                addError(errors, 'userVisaValidUpto', 'Valid Upto cannot be in the past');
            }
        }
    }

    return errors;
};

const hasTaxResidencyCountryValue = (data: UserTaxInfoDto): boolean => {
    const id = data.taxResidencyCountryId;
    if (id !== null && id !== undefined && !Number.isNaN(Number(id))) {
        return true;
    }
    const raw = data.taxResidencyCountry;
    if (raw === null || raw === undefined) return false;
    if (typeof raw === 'number') return !Number.isNaN(raw);
    return String(raw).trim() !== '';
};

/**
 * @param opts.skipForeignTaxFields true for RI (user 2026-09-05): the TIN / TIN-type /
 * tax-residency-certificate inputs are HIDDEN for Resident Indians — a hidden mandatory
 * field must never block the save.
 */
export const validateTaxInformation = (
    data: UserTaxInfoDto,
    opts: { skipForeignTaxFields?: boolean } = {}
): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!hasTaxResidencyCountryValue(data)) {
        addError(errors, 'taxResidencyCountry', 'Tax Residency Country is required');
    }
    // The FATCA US-person question is a legal self-certification: it must be answered explicitly,
    // never defaulted (the printed booklet leaves the cell blank until it is).
    if (!['YES', 'NO'].includes((data.usPersonFatca ?? '').trim().toUpperCase())) {
        addError(errors, 'usPersonFatca', 'Please answer the FATCA question: are you a US person? (Yes / No)');
    }
    if (opts.skipForeignTaxFields) {
        // RI branch (user 2026-09-05): the tab carries PAN details instead of the
        // foreign-tax fields. DOB is display-only (validated on Personal Info).
        const pan = (data.taxPanNo ?? '').trim().toUpperCase();
        if (!pan) {
            addError(errors, 'taxPanNo', 'PAN number is required.');
        } else if (pan.length !== 10) {
            addError(errors, 'taxPanNo', 'PAN number must be exactly 10 characters.');
        } else if (!PAN_REGEX.test(pan)) {
            addError(errors, 'taxPanNo', 'Invalid PAN number format. Example: ABCDE1234F');
        }
        if (isEmpty(data.taxPanFirstName)) {
            addError(errors, 'taxPanFirstName', 'Name on PAN is required.');
        } else if (!NAME_REGEX.test(data.taxPanFirstName!)) {
            addError(errors, 'taxPanFirstName', 'Name on PAN must contain only letters and spaces.');
        }
        return errors;
    }
    if (isEmpty(data.tinNumber)) addError(errors, 'tinNumber', 'Tax Identification Number (TIN) is required');
    if (isEmpty(data.taxIdentificationNumberType)) addError(errors, 'taxIdentificationNumberType', 'TIN Type is required');
    // Laravel blade parity: certificate date is optional, but its calendar caps at today.
    if (data.taxResidencyCertificateDate && data.taxResidencyCertificateDate > localTodayIso()) {
        addError(errors, 'taxResidencyCertificateDate', 'Certificate Date cannot be in the future');
    }
    // Laravel Section4 does not collect FATCA Status / CRS Declaration — no required checks (React-only fields).

    return errors;
};

export const validateBankDetails = (data: UserBankDetailsDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (isEmpty(data.accountType)) addError(errors, 'accountType', 'Account Type is required');

    // Radios send 'YES'/'NO' (uppercase, shared/constants.ts convention) — compare case-insensitively.
    if ((data.rbiApproval ?? '').toLowerCase() === 'yes') {
        if (isEmpty(data.rbiApprovalOrderNumber)) {
            addError(errors, 'rbiApprovalOrderNumber', 'PIS Approval No is required');
        } else if (!/^[a-zA-Z0-9/\-\s]+$/.test(data.rbiApprovalOrderNumber!)) {
            // Laravel rule: letters, digits, slashes, hyphens, spaces.
            addError(errors, 'rbiApprovalOrderNumber', 'PIS Approval Number contains invalid characters');
        }
        if (isEmpty(data.rbiApprovalDate)) {
            addError(errors, 'rbiApprovalDate', 'PIS Approval Date is required');
        } else if (data.rbiApprovalDate! > localTodayIso()) {
            // Approval is an already-issued document: today is allowed, future is not.
            addError(errors, 'rbiApprovalDate', 'PIS Approval Date cannot be in the future');
        }
        if (isEmpty(data.beneficiaryName)) addError(errors, 'beneficiaryName', 'Beneficiary Name is required for PIS Approval');
    } else {
        // If not PIS, Beneficiary Name is just the normal account holder name.
        if (isEmpty(data.beneficiaryName)) addError(errors, 'beneficiaryName', "Account Holder's Name is required");
    }
    if (data.beneficiaryName && !errors.beneficiaryName && !LETTERS_SPACES_REGEX.test(data.beneficiaryName)) {
        addError(errors, 'beneficiaryName', 'Beneficiary name must contain only letters and spaces.');
    }

    if (isEmpty(data.bankName)) {
        addError(errors, 'bankName', 'Bank Name is required');
    } else if (!LETTERS_SPACES_REGEX.test(data.bankName!)) {
        addError(errors, 'bankName', 'Bank name must contain only letters and spaces.');
    }
    if (isEmpty(data.bankAccountNumber)) {
        addError(errors, 'bankAccountNumber', 'Account Number is required');
    } else if (!/^\d+$/.test(String(data.bankAccountNumber).trim())) {
        addError(errors, 'bankAccountNumber', 'Bank account number must be numeric.');
    }
    // IFSC format (user 2026-09-08). Laravel validates only `required|string`, so a 35-character
    // string with spaces reached a regulatory PDF ("C GBQUAGWXDIYAWGIY84GR78 HHWF JKAHFN",
    // dev investor 202609044101). Deliberately STRICTER than Laravel. RBI format: 4 letters
    // (bank), '0' (reserved), then 6 alphanumerics. Case-insensitive so 'hdfc0001234' passes.
    if (isEmpty(data.bankIfscCode)) {
        addError(errors, 'bankIfscCode', 'IFSC Code is required');
    } else if (!IFSC_REGEX.test(data.bankIfscCode!.trim().toUpperCase())) {
        addError(errors, 'bankIfscCode', 'Enter a valid IFSC code (e.g. HDFC0001234).');
    }
    if (isEmpty(data.branchName)) {
        addError(errors, 'branchName', 'Branch Name is required');
    } else if (!LETTERS_SPACES_REGEX.test(data.branchName!)) {
        addError(errors, 'branchName', 'Branch name must contain only letters and spaces.');
    }
    if (isEmpty(data.bankBranchAddress)) addError(errors, 'bankBranchAddress', 'Bank Address is required');

    // Laravel Section5 also requires the structured branch address (InnerPageController L3001-3010).
    if (isEmpty(data.bankDetailsMicr)) {
        addError(errors, 'bankDetailsMicr', 'MICR Number is required');
    } else if (!/^\d{9}$/.test(String(data.bankDetailsMicr))) {
        addError(errors, 'bankDetailsMicr', 'MICR must be exactly 9 digits');
    }
    if (isEmpty(data.bankDetailsCountry)) addError(errors, 'bankDetailsCountry', 'Please select country.');
    if (isEmpty(data.bankDetailsState)) addError(errors, 'bankDetailsState', 'State is required');
    if (isEmpty(data.bankDetailsCity)) {
        addError(errors, 'bankDetailsCity', 'City is required');
    } else if (String(data.bankDetailsCity).length > 100) {
        addError(errors, 'bankDetailsCity', 'City must not exceed 100 characters.');
    }
    if (isEmpty(data.bankDetailsZipCode)) {
        addError(errors, 'bankDetailsZipCode', 'Zip Code is required');
    } else if (!ZIP_CODE_REGEX.test(String(data.bankDetailsZipCode).trim())) {
        addError(errors, 'bankDetailsZipCode', 'Zip code must be 3-10 characters (letters, digits, spaces or hyphens).');
    }

    return errors;
};

export const validateContactDetails = (data: UserContactDetailsDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (isEmpty(data.proofOfAddress)) addError(errors, 'proofOfAddress', 'Proof of Address is required');
    if (isEmpty(data.addressLine1)) addError(errors, 'addressLine1', 'Address Line 1 is required');
    if (isEmpty(data.userCountry)) addError(errors, 'userCountry', 'Country is required');
    if (isEmpty(data.userCity)) addError(errors, 'userCity', 'City is required');
    if (isEmpty(data.userState)) addError(errors, 'userState', 'State is required');
    if (isEmpty(data.userZipCode)) {
        addError(errors, 'userZipCode', 'Zip Code is required');
    } else if (!ZIP_CODE_REGEX.test(data.userZipCode!.trim())) {
        addError(errors, 'userZipCode', 'Zip code must be 3-10 characters (letters, digits, spaces or hyphens).');
    }

    if (isEmpty(data.primaryPhone)) addError(errors, 'primaryPhone', 'Primary Phone is required');
    if (isEmpty(data.email)) {
        addError(errors, 'email', 'Email is required');
    } else if (!EMAIL_REGEX.test(data.email!)) {
        addError(errors, 'email', 'Invalid Email format');
    }


    // Correspondence Address Check
    // Laravel stores "yes"/"no"; older rows may hold "true"/"1".
    const sameAsPermRaw = (data.corrAddressSameAsPerm ?? '').trim().toLowerCase();
    const sameAsPerm = sameAsPermRaw === 'yes' || sameAsPermRaw === 'true' || sameAsPermRaw === '1';
    if (!sameAsPerm) {
        if (isEmpty(data.addressType)) addError(errors, 'addressType', 'Address Type is required for correspondence address');
        if (isEmpty(data.corrAddressLine1)) addError(errors, 'corrAddressLine1', 'Correspondence Address Line 1 is required');
        if (isEmpty(data.corrUserCountry)) addError(errors, 'corrUserCountry', 'Correspondence Country is required');
        if (isEmpty(data.corrUserCity)) addError(errors, 'corrUserCity', 'Correspondence City is required');
        if (isEmpty(data.corrUserState)) addError(errors, 'corrUserState', 'Correspondence State is required');
        if (isEmpty(data.corrUserZipCode)) {
            addError(errors, 'corrUserZipCode', 'Correspondence Zip Code is required');
        } else if (!ZIP_CODE_REGEX.test(data.corrUserZipCode!.trim())) {
            addError(errors, 'corrUserZipCode', 'Correspondence zip code must be 3-10 characters (letters, digits, spaces or hyphens).');
        }
    }

    return errors;
};

// ---- Nomination (Laravel NomineeRequest parity) ----
const NOMINEE_PINCODE_REGEX = /^[A-Za-z0-9 \-]{3,10}$/;
const MOBILE_8_15_REGEX = /^\d{8,15}$/;
/** A national trunk prefix must be dropped once an ISD code is given: "+91 09326738188" is
 *  malformed and reached the submitted PDF that way (2026-09-12). */
const MOBILE_LEADING_ZERO_REGEX = /^0/;
const AADHAAR_REGEX = /^\d{12}$/;
/** Passports vary by issuing country and a nominee may be foreign, so this stays deliberately
 *  permissive — it only rejects punctuation and obviously wrong lengths. */
const PASSPORT_DOC_REGEX = /^[A-Za-z0-9]{6,12}$/;

/**
 * Format rule for an identity document, chosen by the type the investor picked.
 * Returns an error message, or null when the value is acceptable.
 *
 * Presence was the ONLY check before, so "PAN" + a 15-digit number submitted cleanly and
 * printed on the investor document (QA #18, reproduced live 2026-09-12). Stricter than
 * Laravel, which has no rule at all — same product decision as the IFSC format.
 */
const documentNumberError = (docType: unknown, docNo: unknown): string | null => {
    const type = String(docType ?? '').trim().toUpperCase();
    const value = String(docNo ?? '').trim();
    if (!type || !value) return null;               // emptiness is reported separately
    if (type === 'PAN') {
        return PAN_REGEX.test(value.toUpperCase()) ? null : 'Enter a valid PAN (e.g. ABCPE1234F).';
    }
    if (type === 'AADHAAR') {
        return AADHAAR_REGEX.test(value.replace(/\s/g, '')) ? null : 'Aadhaar must be 12 digits.';
    }
    if (type === 'PASSPORT') {
        return PASSPORT_DOC_REGEX.test(value) ? null : 'Enter a valid passport number (6-12 letters or digits).';
    }
    return null;                                     // 'OTHER' — no shape to enforce
};

/** Same rule as the investor's own age check — one arithmetic for every age in the app. */
const nomineeIsMinorFromDob = (dob?: string): boolean => {
    if (!dob) return false;
    const age = ageInYears(dob);
    return !isNaN(age) && age < 18;
};

const isFutureOrToday = (dateStr?: string): boolean => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d >= today;
};

/** Validate one nominee (1/2/3) against NomineeRequest rules — adult vs minor (DOB-derived) branches. */
const validateNominee = (errors: Record<string, string>, d: any, i: 1 | 2 | 3) => {
    const g = (k: string) => d[`${k}${i}`];
    const share = g('nomineeShare');
    const dob = g('nomineeDob');

    if (isEmpty(g('nomineeName'))) addError(errors, `nomineeName${i}`, 'Nominee name is required');
    if (isEmpty(g('nomineeRelation'))) addError(errors, `nomineeRelation${i}`, 'Please select relationship');

    if (share === undefined || share === null || String(share) === '') {
        addError(errors, `nomineeShare${i}`, 'Nominee share is required');
    } else {
        const s = Number(share);
        if (Number.isNaN(s) || s < 1 || s > 100) addError(errors, `nomineeShare${i}`, 'Nominee share must be between 1% and 100%');
    }

    if (isEmpty(dob)) addError(errors, `nomineeDob${i}`, 'Nominee date of birth is required');
    else if (isFutureOrToday(dob)) addError(errors, `nomineeDob${i}`, 'Date of birth must be before today');

    if (isEmpty(g('nomineeAddress'))) addError(errors, `nomineeAddress${i}`, 'Nominee address is required');
    if (isEmpty(g('nomineeCountry'))) addError(errors, `nomineeCountry${i}`, 'Nominee country is required');
    if (isEmpty(g('nomineeState'))) addError(errors, `nomineeState${i}`, 'Nominee state is required');
    if (isEmpty(g('nomineeCity'))) addError(errors, `nomineeCity${i}`, 'Nominee city is required');

    const pincode = g('nomineePincode');
    if (isEmpty(pincode)) addError(errors, `nomineePincode${i}`, 'Nominee pincode is required');
    else if (!NOMINEE_PINCODE_REGEX.test(String(pincode))) addError(errors, `nomineePincode${i}`, 'Pincode must be 3-10 characters (letters, digits, spaces or hyphens)');

    if (nomineeIsMinorFromDob(dob)) {
        // Guardian required (minor)
        if (isEmpty(g('guardianName'))) addError(errors, `guardianName${i}`, 'Guardian name is required');
        if (isEmpty(g('guardianDocType'))) addError(errors, `guardianDocType${i}`, 'Guardian document type is required');
        if (isEmpty(g('guardianDocNo'))) addError(errors, `guardianDocNo${i}`, 'Guardian document number is required');
        if (isEmpty(g('guardianCountrycode'))) addError(errors, `guardianCountrycode${i}`, 'Guardian country code is required');
        const gMobile = g('guardianMobile');
        if (isEmpty(gMobile)) addError(errors, `guardianMobile${i}`, 'Guardian mobile is required');
        else if (!MOBILE_8_15_REGEX.test(String(gMobile))) addError(errors, `guardianMobile${i}`, 'Mobile number must be between 8 to 15 digits');
        else if (MOBILE_LEADING_ZERO_REGEX.test(String(gMobile).trim())) addError(errors, `guardianMobile${i}`, 'Drop the leading 0 — the ISD code already identifies the country.');
        const gEmail = g('guardianEmail');
        if (isEmpty(gEmail)) addError(errors, `guardianEmail${i}`, 'Guardian email is required');
        else if (!EMAIL_REGEX.test(String(gEmail))) addError(errors, `guardianEmail${i}`, 'Enter a valid email address');
        const gPan = g('guardianPanNo');
        if (isEmpty(gPan)) addError(errors, `guardianPanNo${i}`, 'Guardian PAN is required');
        // the field IS a PAN, so it gets the PAN shape — it had no format rule at all before
        else if (!PAN_REGEX.test(String(gPan).trim().toUpperCase())) addError(errors, `guardianPanNo${i}`, 'Enter a valid PAN (e.g. ABCPE1234F).');
        const gDob = g('guardianDob');
        if (isEmpty(gDob)) addError(errors, `guardianDob${i}`, 'Guardian date of birth is required');
        else if (isFutureOrToday(gDob)) addError(errors, `guardianDob${i}`, 'Guardian date of birth must be before today');
    } else {
        // Adult contact + document required
        const email = g('nomineeEmail');
        if (isEmpty(email)) addError(errors, `nomineeEmail${i}`, 'Nominee email is required');
        else if (!EMAIL_REGEX.test(String(email))) addError(errors, `nomineeEmail${i}`, 'Enter a valid email address');
        const mobile = g('nomineeMobile');
        if (isEmpty(mobile)) addError(errors, `nomineeMobile${i}`, 'Nominee mobile number is required');
        else if (!MOBILE_8_15_REGEX.test(String(mobile))) addError(errors, `nomineeMobile${i}`, 'Mobile number must be between 8 to 15 digits');
        else if (MOBILE_LEADING_ZERO_REGEX.test(String(mobile).trim())) addError(errors, `nomineeMobile${i}`, 'Drop the leading 0 — the ISD code already identifies the country.');
        if (isEmpty(g('nomineeDocType'))) addError(errors, `nomineeDocType${i}`, 'Select document type');
        if (isEmpty(g('nomineeDocNo'))) addError(errors, `nomineeDocNo${i}`, 'Enter document number');
        else {
            const docErr = documentNumberError(g('nomineeDocType'), g('nomineeDocNo'));
            if (docErr) addError(errors, `nomineeDocNo${i}`, docErr);
        }
    }
};

export const validateNominationDetails = (data: UserNominationDto): Record<string, string> => {
    const errors: Record<string, string> = {};
    const appointNominee = data.appointNominee === true || data.appointNominee === 'yes';
    const declined = data.appointNominee === false || data.appointNominee === 'no';
    // The Yes/No question itself is mandatory: an unanswered tab must not save as "No".
    if (!appointNominee && !declined) {
        addError(errors, 'appointNominee', 'Please choose Yes or No for the nominee question');
        return errors;
    }
    if (!appointNominee) return errors; // No validation when not appointing nominee

    const d = data as any;

    // Laravel (validateNomineeRelations): each nominee must have a distinct
    // relation, compared case-insensitively and only once two are filled in.
    const relations = [d.nomineeRelation1, d.nomineeRelation2, d.nomineeRelation3]
        .map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : ''))
        .filter((v) => v !== '' && v !== 'null' && v !== 'undefined');
    if (relations.length >= 2 && new Set(relations).size !== relations.length) {
        addError(errors, 'nomineeRelation1', 'Each nominee must have a unique relation.');
    }

    const share1 = Number(d.nomineeShare1) || 0;
    const share2 = Number(d.nomineeShare2) || 0;
    const share3 = Number(d.nomineeShare3) || 0;
    const has = (v: any) => v !== undefined && v !== null && String(v) !== '';

    // Laravel: nominee 1 always; nominee 2 unless share1>=100; nominee 3 unless share1+share2>=100.
    validateNominee(errors, d, 1);
    if (has(d.nomineeShare1) && share1 < 100) validateNominee(errors, d, 2);
    if (has(d.nomineeShare2) && (share1 + share2) < 100) validateNominee(errors, d, 3);

    // Total allocation must equal exactly 100% (Laravel withValidator).
    if (share1 + share2 + share3 !== 100) {
        addError(errors, 'nomineeShare1', 'Total nominee share must be exactly 100%');
    }

    return errors;
};

export const validateRiskProfile = (data: UserRiskProfileDto | InvestorExperienceDto): Record<string, string> => {
    const errors: Record<string, string> = {};
    const d = data as any;

    if (isEmpty(data.occupation)) addError(errors, 'occupation', 'Occupation is required');
    if (isEmpty(data.grossIncome) && isEmpty(d.annualIncome)) addError(errors, 'grossIncome', 'Gross Income is required');
    if (isEmpty(data.netWorth)) addError(errors, 'netWorth', 'Net Worth is required');
    if (isEmpty(data.educationalQualification) && isEmpty(d.qualification)) addError(errors, 'educationalQualification', 'Educational Qualification is required');
    if (isEmpty(data.sourceOfFunds)) addError(errors, 'sourceOfFunds', 'Source of Funds is required');
    // Stricter than Laravel by product decision (user 2026-09-07): Laravel leaves Source of
    // Wealth and Line of Business optional. Safe to tighten — every existing row already
    // carries both, so no investor is retro-blocked when they re-open the tab.
    if (isEmpty(d.sourceOfWealth)) addError(errors, 'sourceOfWealth', 'Source of Wealth is required');
    if (isEmpty(d.lineOfBusiness)) addError(errors, 'lineOfBusiness', 'Line of Business / Industry is required');
    if (isEmpty(d.natureOfOrganisation)) addError(errors, 'natureOfOrganisation', 'Nature of organisation is required');

    const expIn = data.investmentExperienceIn;
    if (!expIn || expIn.length === 0) addError(errors, 'investmentExperienceIn', 'Please select at least one Investment Experience category');

    if (isEmpty(d.investmentExperienceYears) && isEmpty(d.yearsOfInvestmentExperience)) {
        addError(errors, 'investmentExperienceYears', 'No of Years of Investment Experience is required');
    }

    if (d.polExposed === undefined || d.polExposed === null) addError(errors, 'polExposed', 'Please indicate if you are a politically exposed person');
    if (d.polExposedRelated === undefined || d.polExposedRelated === null) addError(errors, 'polExposedRelated', 'Please indicate if you are related to a politically exposed person');
    // Laravel: the 'activity' rule is commented out (no answerable field) — do not require it.
    if (d.moneyChangeService === undefined || d.moneyChangeService === null) addError(errors, 'moneyChangeService', 'Foreign Exchange / Money Changer Services is required');
    if (d.gamblingService === undefined || d.gamblingService === null) addError(errors, 'gamblingService', 'Gaming / Gambling / Lottery Services is required');
    if (d.pawningService === undefined || d.pawningService === null) addError(errors, 'pawningService', 'Money Lending / Pawning Services is required');
    if (d.instanceViolation === undefined || d.instanceViolation === null) addError(errors, 'instanceViolation', 'Instance of violation question is required');

    return errors;
};
